import { chakra, Icon, type ButtonProps } from '@chakra-ui/react';
import { LuBell } from 'react-icons/lu';
import ShortcutTooltip from '@/components/common/review/ShortcutTooltip';
import type { PlanNodeOut } from '@/client';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import { Tooltip } from '@/components/ui/tooltip';
import { canElectToSchedule } from '@/components/maintenance/model/planReviewFilter';

/** Said in place of the button when the card has no date to put on a schedule. */
export const NO_DATE_REASON = 'This task has no due date, so there is nothing to remind you against.';

/**
 * What the three verdicts are for, said once above them.
 *
 * The buttons name the verbs; this names the choice. Held beside {@link NO_DATE_REASON} because the
 * same rule decides between them — an undated card cannot be scheduled at all, and a sentence
 * offering a verdict its button refuses is worse than no sentence.
 */
export const VERDICT_GUIDANCE =
	"Discard anything that isn't right, schedule what you want reminders about, and keep the rest unscheduled.";

/**
 * The same choice, put to a household reading one card rather than working through a pass.
 *
 * There is no "the rest" in a drawer, and the plural reads as though the other tasks were settled by
 * this answer. Only this one is.
 */
export const CARD_VERDICT_GUIDANCE =
	"Discard it if it isn't right, schedule it if you want reminders about it, or keep it unscheduled.";

export const UNDATED_VERDICT_GUIDANCE =
	'This task has no date, so it can be discarded or kept unscheduled. It cannot be scheduled without one.';

interface KeepOnScheduleButtonProps {
	node: PlanNodeOut;
	onKeep: () => void;
	isPending?: boolean;
	size?: 'sm' | 'lg';
	isFullWidth?: boolean;
	width?: ButtonProps['width'];
	/**
	 * One word on every surface. A verdict that fits a 360px screen needs no second spelling, which
	 * is what the bell beside it bought — the icon says which of the two keeps this is, and the
	 * label no longer has to.
	 */
	label?: string;
	/** Uses the shared three-verdict responsive recipe. */
	inVerdictRow?: boolean;
	/**
	 * The key that presses this, where something is listening for it.
	 *
	 * The pass binds one; the drawer does not, and a tooltip naming a key that does nothing there
	 * would be worse than no tooltip. Absent, neither the hint nor `aria-keyshortcuts` is drawn.
	 */
	shortcutKey?: string;
}

/**
 * Keep this card as a priority — withheld from one that has no date to remind against.
 *
 * Its own component because two surfaces offer this verdict, the review pass and the drawer footer,
 * and the rule withholding it is not obvious enough to be worth stating twice. The endpoint answers
 * the election with a 422 on an undated card, since a reminder counts back from a date and there is
 * none, so the button would be one that cannot work.
 *
 * Disabled rather than hidden: a card that is one date away from being schedulable should say so,
 * and a control that quietly disappears reads as a bug in the pass rather than a fact about the card.
 */
const KeepOnScheduleButton = ({
	node,
	onKeep,
	isPending = false,
	size = 'sm',
	isFullWidth = false,
	width,
	label = 'Schedule',
	inVerdictRow = false,
	shortcutKey,
}: KeepOnScheduleButtonProps) => {
	const canSchedule = canElectToSchedule(node);
	const buttonWidth = width ?? (isFullWidth ? 'full' : 'auto');

	const button = (
		<PrimaryButton
			size={size}
			elevated
			gap={1.5}
			w={buttonWidth}
			verdictRow={inVerdictRow}
			bg="brand.solid"
			color="brand.contrast"
			_hover={{ bg: 'brand.solidHover', color: 'brand.contrast' }}
			onClick={onKeep}
			loading={isPending}
			disabled={!canSchedule}
			aria-label={label}
			// Withheld with the button: the pass binds this key only where the card can be scheduled, so
			// an undated one would be announcing a shortcut that does nothing.
			aria-keyshortcuts={canSchedule ? shortcutKey : undefined}
		>
			{label}
			<Icon as={LuBell} boxSize={4} />
		</PrimaryButton>
	);

	// The undated tooltip wins over the shortcut hint below: a control that cannot be pressed has
	// something more useful to say than which key would press it.
	if (canSchedule)
		return shortcutKey ? (
			<Tooltip content={<ShortcutTooltip action={`${label} task`} shortcutKey={shortcutKey} />} openDelay={300}>
				{/* The span is load-bearing, not layout: `Tooltip.Trigger` is `asChild`, and
				    `PrimaryButton` forwards no ref for it to attach to. Removing it silently kills the
				    tooltip. */}
				<chakra.span display="inline-flex" w={buttonWidth} minW={0}>
					{button}
				</chakra.span>
			</Tooltip>
		) : (
			button
		);

	// A disabled control takes no focus and fires no pointer events, so the tooltip wraps a span that
	// still does — otherwise the explanation is reachable by neither keyboard nor hover.
	return (
		<Tooltip content={NO_DATE_REASON} openDelay={0}>
			<chakra.span tabIndex={0} aria-label={NO_DATE_REASON} display="inline-flex" w={buttonWidth} minW={0}>
				{button}
			</chakra.span>
		</Tooltip>
	);
};

export default KeepOnScheduleButton;
